/**
 * BeforeAfterRecap
 * ================
 *
 * A side-by-side (or stacked on mobile) before/after comparison card used
 * in the confirmation step of informant management flows.  Shows the user
 * what the assignment will look like before and after the operation.
 *
 * Each column lists informants and their assigned life stage(s).  Changes
 * are visually highlighted to make it obvious what's moving.
 */

import React from 'react';

/**
 * @param {Object} props
 * @param {Array}  props.before – Array of { name, lifeStages: string[] }
 * @param {Array}  props.after  – Array of { name, lifeStages: string[], isNew?: boolean }
 * @param {string} [props.operationLabel] – e.g. "Switch Questionnaires" / "Add Second Key Contact"
 * @param {string} [props.emptyLabel]     – Fallback text when no entries exist (defaults to "None assigned")
 */
export default function BeforeAfterRecap({ before = [], after = [], operationLabel, emptyLabel = 'None assigned' }) {
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      {operationLabel && (
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {operationLabel}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
        {/* BEFORE */}
        <div className="p-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Before
          </h4>
          <ul className="space-y-2">
            {before.length > 0
              ? before.map((entry, i) => (
                  <RecapRow key={entry.id || `before-${i}`} {...entry} />
                ))
              : <li className="text-xs text-gray-500 italic">{emptyLabel}</li>}
          </ul>
        </div>

        {/* AFTER */}
        <div className="p-3 bg-violet-50/30">
          <h4 className="text-xs font-semibold text-violet-700 uppercase tracking-wider mb-2">
            After
          </h4>
          <ul className="space-y-2">
            {after.length > 0
              ? after.map((entry, i) => (
                  <RecapRow key={entry.id || `after-${i}`} {...entry} />
                ))
              : <li className="text-xs text-gray-500 italic">{emptyLabel}</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function RecapRow({ name = 'Unknown', lifeStages = [], isNew }) {
  const stages = Array.isArray(lifeStages) ? lifeStages : [];
  return (
    <li className="flex items-start gap-2 text-xs text-gray-700">
      <div className="flex-1">
        <span className="font-semibold">{name}</span>
        {isNew && (
          <span className="ml-1.5 inline-block px-1.5 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded">
            New
          </span>
        )}
        <div className="text-gray-500 mt-0.5">
          {stages.length > 0
            ? stages.join(' & ')
            : 'No questionnaire assigned'}
        </div>
      </div>
    </li>
  );
}
